import React, { useState } from 'react';
import styles from './_user-editor.module.scss';
import { Button, InputAdornment, Tooltip } from '@mui/material';
import produce from 'immer';
import { includes, isNil, reject } from 'ramda';
import {
  Card, CardContent, Grid, TextField, Checkbox,
  List, ListItem, ListItemText, ListItemSecondaryAction
} from "@mui/material";
import { User } from '../../../Models/User';
import { Client, hasValue } from '@amsanalytics/react-shared-library';
import { ILookupItem } from '../../../Models/LookupItem';
import { Organization } from '../../../Models/Organization';
import { httpGetAsync } from '../../../Http/HttpClient';
import { IAuthService } from '../../../Services/AuthService';
import { appSettings } from '../../..';
import { isInRole } from '../../../Helpers/Utilities';
import { NiadaAdministrator } from '../../../Helpers/Constants';
import LoadingPanel from '../LoadingPanel/LoadingPanel';

export interface UserEditorProps {
  user?: User;
  adUserIds: string[]; // current ad user ids. used to prevent duplicate ad user ids when creating a new user
  currentUser: User | null;
  users: User[];
  clientId: string;
  roles: ILookupItem[];
  authService: IAuthService;
  organizations: Organization[];
  onCreateUser: (user: User) => void;
  onUpdateUser: (user: User) => void;
  onClose: () => void;
}

export default function UserEditor(props: UserEditorProps) {
  const [user, setUser] = useState<Partial<User>>(props.user
    ? {
      ...props.user,
      employeeRoleIds: props.user.employeeRoleIds ?? [],
      employeeReportsToEmployeeIds: props.user.employeeReportsToEmployeeIds ?? [],
      employeesWhoReportToThisEmployeeIds: props.user.employeesWhoReportToThisEmployeeIds ?? [],
      reportsToIds: props.user.reportsToIds ?? []
    }
    : {
      isActive: true,
      isCrmUser: true,
      employeeRoleIds: [],
      employeeReportsToEmployeeIds: [],
      employeesWhoReportToThisEmployeeIds: []
    }
  );
  const [emailValidationResult, setEmailValidationResult] = useState<1 | 2 | 3 | 4 | null>(props.user ? 3 : null);
  const [emailValidationError, setEmailValidationError] = useState<string>("");
  const [updateButtonClicked, setUpdateButtonClicked] = useState<boolean>(false);
  const [isValidatingEmail, setIsValidatingEmail] = useState<boolean>(false);
  const updateUser = <T extends {} | null>(
    value: T,
    fieldName: string
  ): void => {
    // user![fieldName] = value;
    const result = produce(user, user => {
      user[fieldName] = value;
    });
    setUser(result);

  };
  const updateUserRoles = (roleId: string, addRole: boolean) => {
    const result = produce(user, user => {
      if (addRole) {
        if (!includes(roleId, user.employeeRoleIds!)) {
          user.employeeRoleIds!.push(roleId);
        }
      } else {
        user.employeeRoleIds = reject(erId => erId === roleId, user.employeeRoleIds!);
      }
    });
    setUser(result);
  }

  // Whether or not the user can click the "Create" button.
  const canCreateUser = (): boolean => {
    return hasValue(user.adUserId)
      && !newUserInvalidAdUserId()
      && !isCustomEmailDomain()
      && hasValue(user.emailAddress)
      && hasValue(user.firstName)
      && hasValue(user.lastName)
      && emailValidationResult !== 1
      && emailValidationResult !== 4;
  }

  // Whether or not the user can click the "Update" button.
  const canUpdateUser = (): boolean => {
    return hasValue(user.emailAddress)
      && hasValue(user.firstName)
      && hasValue(user.lastName) 
      && (emailValidationResult === null || emailValidationResult === 3);
  }

  // Returns true if the given ad user id is already in use, otherwise false.
  const newUserInvalidAdUserId = (): boolean => {
    return !isNil(user) && !isNil(user.adUserId) && includes(user.adUserId, props.adUserIds);
  }

  // Returns true if AD User id contains an @ symbol, implying the user is trying to enter a custom email domain.
  const isCustomEmailDomain = (): boolean => {
    return !isNil(user) && !isNil(user.adUserId) && user.adUserId.includes('@');
  }

  // Returns true if the user can edit the roles, false if not.
  const showRoleCheckboxes = (): boolean => {
    return props.currentUser !== null && props.currentUser !== undefined 
      && (props.currentUser.isSuperUser === true || isInRole(props.currentUser.employeeRoleIds, NiadaAdministrator));
  }

  // Validate email address
  const validateEmail = async (email: string) => {
    const token: string = await props.authService.getToken();
    const response = await httpGetAsync(`${appSettings.GetWebApiBaseUri()}/User/validateUserAccount/${email}/${props.clientId}`, token);
    if(response) {
      if(response.ok) {
        var result: {validationResult: 1 | 2 | 3 | 4; userInfo?: {firstName: string; lastName: string, adUserId: string, phoneNumber: string}} = await response.json();
        setEmailValidationResult(result.validationResult);
        if(result.validationResult === 1) {
          setEmailValidationError("Emails exists for user of another dealer.");           
        }
        else if(result.validationResult === 4) {
          setEmailValidationError("NIADA user already exists for this dealer.");
        } else if (result.validationResult === 2) { // User exists in same client but is not a NIADA user yet.
          if(result.userInfo?.adUserId.endsWith('@amsanalytics.com')) {
            setEmailValidationError("");
            if(result.userInfo) {
              setUser({
                ...user,
                firstName: result.userInfo.firstName,
                lastName: result.userInfo.lastName,
                adUserId:  result.userInfo.adUserId.replace(/@.*/, ""),
                mobilePhone: result.userInfo.phoneNumber
              });
            }
          } else {
            setEmailValidationError("The ad user id for this user is using a custom domain which is not allowed.");
          }
        } else {
          setEmailValidationError("");
        }                
      } else {
        setEmailValidationError("");
        setEmailValidationResult(null);
      }
    }
    setIsValidatingEmail(false);
  }
  
  const disableFields = (props.user && props.user.emailAddress?.toLowerCase() !== user.emailAddress?.toLowerCase() && emailValidationResult !== 3)
  || (!props.user && (emailValidationResult === 2 || emailValidationResult === null));

  return (
    <div className={styles.userEditorContainer}>
      <div className={styles.inputs}>
        <div style={{ maxHeight: 'calc(100vh - 105px)', overflowY: 'auto' }}>
          <Card style={{ width: '100%', marginBottom: '5px' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label='Email Address'
                    onChange={e => {
                      updateUser<string>(e.target.value, "emailAddress");
                      // setEmailIsValid(false);                      
                      setEmailValidationResult(null);
                    }}
                    onBlur={() => {
                      if(user.emailAddress) {
                        setIsValidatingEmail(true);
                        validateEmail(user.emailAddress);
                      }
                    }}
                    value={user.emailAddress}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    required={true}
                    error={hasValue(emailValidationError) || (updateButtonClicked && !hasValue(user.emailAddress))}
                    helperText={emailValidationError ? emailValidationError : ""}
                  />
                </Grid>
                <Grid item xs={12}>
                  {
                    isNil(props.user) && (
                      <TextField
                        required
                        label='Ad User Id'
                        value={user.adUserId}
                        onChange={e => updateUser<string>(e.target.value, "adUserId")}
                        fullWidth
                        disabled={disableFields}
                        error={newUserInvalidAdUserId() || isCustomEmailDomain() || (updateButtonClicked && !hasValue(user.adUserId))}
                        helperText={newUserInvalidAdUserId() ? "This Ad User Id is already in use." : isCustomEmailDomain() ? "Custom email domains are not allowed" : ""}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">@amsanalytics.com</InputAdornment>,
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    )
                  }
                  {
                    !isNil(props.user) && (
                      <Tooltip title='Ad User Id cannot be manually changed for existing users'>
                        <TextField
                          label='Ad User Id'
                          value={user.adUserId}
                          disabled={true}
                          fullWidth                          
                          error={(updateButtonClicked && !hasValue(user.adUserId))}
                        />
                      </Tooltip>
                    )
                  }
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label='First Name'
                    onChange={e => updateUser<string>(e.target.value, "firstName")}
                    value={user.firstName}
                    fullWidth
                    required={true}
                    InputLabelProps={{ shrink: true }}
                    disabled={disableFields}
                    error={updateButtonClicked && !hasValue(user.firstName)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label='Last Name'
                    onChange={e => updateUser<string>(e.target.value, "lastName")}
                    value={user.lastName}
                    fullWidth
                    required={true}
                    InputLabelProps={{ shrink: true }}
                    disabled={disableFields}
                    error={updateButtonClicked && !hasValue(user.lastName)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label='Mobile Phone'
                    onChange={e => updateUser<string>(e.target.value, "mobilePhone")}
                    value={user.mobilePhone}
                    disabled={disableFields}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                {
                  showRoleCheckboxes() && (
                    <div style={{ margin: '15px 0 0 15px', width: '100%' }}>
                      <h3 style={{ margin: 0, padding: '0 0 5px 5px' }}>NIADA Roles</h3>
                      <List dense style={{ marginTop: 0, paddingTop: 0 }}>
                        {props.roles && props.roles.sort((first: ILookupItem, second: ILookupItem) => first.name.localeCompare(second.name)).map((role: ILookupItem, key: number) => {
                          const labelId = `roles-label-${key}`;
                          return (
                            <ListItem
                              key={key}
                              style={{ padding: '5px' }}>
                              <ListItemText id={labelId} primary={role.name} secondary={role.description} style={{ fontWeight: 500, maxWidth: 400 }} />
                              <ListItemSecondaryAction>
                                <Checkbox
                                  edge="end"
                                  onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                                    updateUserRoles(role.id!, checked);
                                  }}
                                  checked={includes(role.id, user.employeeRoleIds!)}
                                  inputProps={{ 'aria-labelledby': labelId }}
                                />
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                      </List>
                    </div>
                  )
                }
              </Grid>
            </CardContent>
          </Card>
        </div>
      </div>
      <div className={styles.actions}>
        {user.userId && (
          <Button
            color="primary"
            variant="contained"
            title="Update"
            disabled={!canUpdateUser()}
            onClick={() => {
              setUpdateButtonClicked(true);
              if (canUpdateUser()) {
                props.onUpdateUser(user as User);
              }
            }}
          >
            Update
          </Button>
        )}
        {!user.userId && (
          <Button
            color="primary"
            variant="contained"
            title="Create"
            disabled={!canCreateUser()}
            onClick={() => {
              setUpdateButtonClicked(true);
              if (canCreateUser()) {
                // For new users: append AMS email domain to the user's AD User Id input.
                props.onCreateUser({
                  ...user,
                  adUserId: `${user.adUserId}@amsanalytics.com`
                } as User)
              }
            }}
          >
            Create
          </Button>
        )}
        <Button color="secondary" variant="contained" title="Cancel" style={{ marginLeft: "10px" }}
          onClick={props.onClose}
        >
          Cancel
        </Button>
      </div>
      {isValidatingEmail && (
        <LoadingPanel style={{ position: 'absolute', top: '0px', left: '0px', width: '100%', height: '600px' }} label={`Validating Email`} />
      )}
    </div>
  );
}
